function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
import { isElementOfType } from '@deephaven/react-hooks';
import { ensureArray } from '@deephaven/utils';
import { Item, Section } from "../shared.js";
import { Text } from "../Text.js";
import ItemContent from "../ItemContent.js";
/**
 * `Item.textValue` prop needs to be a non-empty string for accessibility
 * purposes. This is not displayed in the UI.
 */
export var ITEM_EMPTY_STRING_TEXT_VALUE = 'Empty';

/**
 * React Spectrum <Section> supports an `ItemRenderer` function as a child. The
 * DH picker makes use of this internally, but we don't want to support it as
 * an incoming prop.
 */

// Picker uses `icon` slot. ListView can use `image` or `illustration` slots.
// https://github.com/adobe/react-spectrum/blob/main/packages/%40react-spectrum/picker/src/Picker.tsx#L194
// https://github.com/adobe/react-spectrum/blob/main/packages/%40react-spectrum/list/src/ListViewItem.tsx#L266-L267

/**
 * Augment the Spectrum selection key type to include boolean values.
 * Spectrum collection components already supports this, but the built in types
 * don't reflect it.
 */

/**
 * Augment the Spectrum selection change handler type to include boolean keys.
 * Spectrum components already supports this, but the built in types don't
 * reflect it.
 */

/**
 * Spectrum collection components support a variety of item types, including
 * strings, numbers, booleans, and more complex React elements. This type
 * represents a normalized form to make rendering items simpler and keep the
 * logic of transformation in separate util methods. It also adheres to the
 * `KeyedItem` interface to be compatible with Windowed data utils
 * (e.g. `useViewportData`).
 */

/**
 * DH wrappers of Spectrum collection components use a normalized item that
 * includes a `key` prop and an optional `item` prop. This is mostly to support
 * Windowed data where items are created before their data has been loaded (data
 * gets set in the `item` prop). If data has loaded, return its `key`. If not,
 * return the top-level `key` on the normalized item.
 * @param item The normalized item or section
 * @returns The `key` of the item or section
 */
export function getItemKey(item) {
  var _item$item$key, _item$item;
  return (_item$item$key = item === null || item === void 0 || (_item$item = item.item) === null || _item$item === void 0 ? void 0 : _item$item.key) !== null && _item$item$key !== void 0 ? _item$item$key : item === null || item === void 0 ? void 0 : item.key;
}

/**
 * Determine Item `textValue` based on the `textValue` prop or primitive children
 * value.
 * @param item The item to get the text value for
 * @returns The text value of the item
 */
export function getItemTextValue(item) {
  if (item.props.textValue == null) {
    var itemKeyStr = item.key == null ? undefined : String(item.key);
    return ['string', 'boolean', 'number'].includes(typeof item.props.children) ? String(item.props.children) : itemKeyStr;
  }
  return item.props.textValue === '' ? ITEM_EMPTY_STRING_TEXT_VALUE : item.props.textValue;
}

/**
 * Get the position of the item with the given selected key in a list of items.
 * @param items The items to search
 * @param itemHeight The height of each item
 * @param selectedKey The key of the selected item
 * @param topOffset The top offset of the list
 * @returns The position of the selected item or the top offset if not found
 */
export function getPositionOfSelectedItemElement(_x) {
  return _getPositionOfSelectedItemElement.apply(this, arguments);
}

/**
 * Determine if a node is a Section element.
 * @param node The node to check
 * @returns True if the node is a Section element
 */
function _getPositionOfSelectedItemElement() {
  _getPositionOfSelectedItemElement = _asyncToGenerator(function* (_ref) {
    var items = _ref.items,
      itemHeight = _ref.itemHeight,
      selectedKey = _ref.selectedKey,
      topOffset = _ref.topOffset;
    var position = topOffset;
    if (selectedKey == null) {
      return position;
    }
    for (var i = 0; i < items.length; i += 1) {
      var item = items[i];
      if (item.key === selectedKey) {
        return position;
      }
      position += itemHeight;
    }
    return topOffset;
  });
  return _getPositionOfSelectedItemElement.apply(this, arguments);
}
export function isSectionElement(node) {
  return isElementOfType(node, Section);
}

/**
 * Determine if a node is an Item element.
 * @param node The node to check
 * @returns True if the node is an Item element
 */
export function isItemElement(node) {
  return isElementOfType(node, Item);
}

/**
 * Determine if a node is an Item element containing a child `Text` element with
 * a `slot` prop set to `description`.
 * @param node The node to check
 * @returns True if the node is an Item element with a description
 */
export function isItemElementWithDescription(node) {
  if (!isItemElement(node)) {
    return false;
  }

  // If children are wrapped in `ItemContent`, go down 1 level
  var children = isElementOfType(node.props.children, ItemContent) ? node.props.children.props.children : node.props.children;
  var childrenArray = ensureArray(children);
  var result = childrenArray.some(child => {
    var _child$props;
    return isElementOfType(child, Text) && ((_child$props = child.props) === null || _child$props === void 0 ? void 0 : _child$props.slot) === 'description';
  });
  return result;
}

/**
 * Determine if a node is an array containing normalized items or sections with
 * keys. Note that this only checks the first node in the array.
 * @param node The node to check
 * @returns True if the node is a normalized item or section with keys array
 */
export function isNormalizedItemsWithKeysList(node) {
  if (!Array.isArray(node)) {
    return false;
  }
  if (node.length === 0) {
    return true;
  }
  return !isItemOrSection(node[0]) && node[0] != null && typeof node[0] === 'object' && 'key' in node[0];
}

/**
 * Determine if an object is a normalized section.
 * @param maybeNormalizedSection The object to check
 * @returns True if the object is a normalized section
 */
export function isNormalizedSection(maybeNormalizedSection) {
  return maybeNormalizedSection.item != null && 'items' in maybeNormalizedSection.item;
}

/**
 * Determine if a node is an item or section. Valid types include strings,
 * numbers, booleans, Item elements, and Section elements.
 * @param node The node to check
 * @returns True if the node is an item or section
 */
export function isItemOrSection(node) {
  return typeof node === 'string' || typeof node === 'number' || typeof node === 'boolean' || isItemElement(node) || isSectionElement(node);
}

/**
 * Returns a TooltipOptions object or null if options is false or null.
 * @param options Tooltip options
 * @param placement Default placement for the tooltip if `options` is set
 * explicitly to `true`
 * @returns TooltipOptions or null
 */
export function normalizeTooltipOptions(options) {
  var placement = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'right';
  if (options == null || options === false) {
    return null;
  }
  if (options === true) {
    return {
      placement
    };
  }
  return options;
}

/**
 * Convert a selection of `ItemKey`s to a selection of strings.
 * @param itemKeys The selection of `ItemKey`s
 * @returns The selection of strings
 */
export function itemSelectionToStringSet(itemKeys) {
  if (itemKeys == null || itemKeys === 'all') {
    return itemKeys;
  }
  return new Set([...itemKeys].map(String));
}
//# sourceMappingURL=itemUtils.js.map